/**
 * Centralized music volume control.
 * All volumes are scaled by the user's persisted musicVolume preference (0-1).
 * Menu/UI scenes use a quieter base level; wave gameplay raises it.
 */
import type * as Phaser from "phaser"
import { AUDIO } from "./keys"
import { PersistedData } from "./persisted-data"

/** Base volume ratios (before user scaling). */
const MENU_RATIO = 0.6
const WAVE_RATIO = 0.85
const FADE_MS = 600

interface SoundManagerWithContext extends Phaser.Sound.BaseSoundManager {
  context: AudioContext
}

declare global {
  interface Window {
    __startMusic?: () => void
  }
}

/** Effective volume = ratio * userPref. */
function effectiveMusic(ratio: number): number {
  return ratio * PersistedData.getMusicVolume()
}

function getMusicSounds(scene: Phaser.Scene): Phaser.Sound.BaseSound[] {
  const soundManager = scene.sound as Phaser.Sound.BaseSoundManager & {
    sounds?: Phaser.Sound.BaseSound[]
  }
  return (soundManager.sounds ?? []).filter((sound: Phaser.Sound.BaseSound) =>
    sound.key.startsWith("bttf_music"),
  )
}

function destroyMusicSound(scene: Phaser.Scene, sound: Phaser.Sound.BaseSound) {
  const s = sound as Phaser.Sound.WebAudioSound
  scene.tweens.killTweensOf(s)
  if (s.isPlaying) s.stop()
  s.destroy()
}

function cleanupDuplicateMusicTracks(scene: Phaser.Scene): void {
  const seenPlayingKeys = new Set<string>()
  for (const sound of getMusicSounds(scene)) {
    const s = sound as Phaser.Sound.WebAudioSound
    if (!s.isPlaying) {
      destroyMusicSound(scene, s)
      continue
    }

    if (seenPlayingKeys.has(s.key)) {
      destroyMusicSound(scene, s)
      continue
    }

    seenPlayingKeys.add(s.key)
  }
}

/** Set all active music tracks to the target volume with a smooth fade. */
function fadeMusicTo(
  scene: Phaser.Scene,
  target: number,
  duration = FADE_MS,
): void {
  for (const sound of getMusicSounds(scene)) {
    const s = sound as Phaser.Sound.WebAudioSound
    if (typeof s.volume === "number" && s.isPlaying) {
      scene.tweens.killTweensOf(s)
      scene.tweens.add({
        targets: s,
        volume: target,
        duration,
        ease: "Sine.easeInOut",
      })
    }
  }
}

/** Lower music for menu / UI scenes. */
export function setMusicMenuVolume(scene: Phaser.Scene): void {
  cleanupDuplicateMusicTracks(scene)
  fadeMusicTo(scene, effectiveMusic(MENU_RATIO))
}

/** Raise music for wave gameplay (still balanced against SFX). */
export function setMusicWaveVolume(scene: Phaser.Scene): void {
  cleanupDuplicateMusicTracks(scene)
  fadeMusicTo(scene, effectiveMusic(WAVE_RATIO))
}

/** Volume to use when creating a new music track in the menu. */
export function getMenuTrackVolume(): number {
  return effectiveMusic(MENU_RATIO)
}

/** Immediately apply current user volume to all playing music tracks (no fade). */
export function applyMusicVolume(scene: Phaser.Scene, isWave = false): void {
  const vol = effectiveMusic(isWave ? WAVE_RATIO : MENU_RATIO)
  for (const sound of getMusicSounds(scene)) {
    ;(sound as Phaser.Sound.WebAudioSound).volume = vol
  }
}

/** Fade out and stop all current music tracks, then call onDone. */
export function fadeMusicOut(
  scene: Phaser.Scene,
  duration = 800,
  onDone?: () => void,
): void {
  cleanupDuplicateMusicTracks(scene)
  const tracks = getMusicSounds(scene)
  if (tracks.length === 0) {
    onDone?.()
    return
  }
  let completed = 0
  for (const sound of tracks) {
    const s = sound as Phaser.Sound.WebAudioSound
    scene.tweens.killTweensOf(s)
    if (!s.isPlaying) {
      completed++
      if (completed === tracks.length) onDone?.()
      continue
    }
    scene.tweens.add({
      targets: s,
      volume: 0,
      duration,
      ease: "Sine.easeIn",
      onComplete: () => {
        scene.tweens.killTweensOf(s)
        s.stop()
        scene.tweens.killTweensOf(s)
        s.destroy()
        completed++
        if (completed === tracks.length) onDone?.()
      },
    })
  }
}

/** Start the boss music track, looping at wave volume. */
export function startBossMusic(scene: Phaser.Scene, key: string): void {
  if (!scene.cache.audio.exists(key)) return
  cleanupDuplicateMusicTracks(scene)
  for (const sound of getMusicSounds(scene)) {
    const s = sound as Phaser.Sound.WebAudioSound
    if (s.key === key && s.isPlaying) return
  }

  const track = scene.sound.add(key, {
    loop: true,
    volume: 0,
  })
  track.play()
  scene.tweens.add({
    targets: track,
    volume: effectiveMusic(WAVE_RATIO),
    duration: FADE_MS,
    ease: "Sine.easeOut",
  })
}

/** Fade out boss music only, then return to the normal wave track. */
export function returnToWaveMusic(
  scene: Phaser.Scene,
  duration = FADE_MS,
): void {
  cleanupDuplicateMusicTracks(scene)
  const bossFadeDuration = Math.max(400, Math.round(duration * 0.68))
  const waveFadeDelay = Math.max(0, Math.round(duration * 0.28))
  const waveFadeDuration = Math.max(400, duration - waveFadeDelay)
  const waveTrack = ensureMusicTrack(scene, AUDIO.MUSIC_01, true)
  if (waveTrack) {
    scene.tweens.killTweensOf(waveTrack)
    scene.tweens.add({
      targets: waveTrack,
      volume: effectiveMusic(WAVE_RATIO),
      duration: waveFadeDuration,
      delay: waveFadeDelay,
      ease: "Sine.easeOut",
    })
  }

  const bossTracks = getMusicSounds(scene).filter(
    (sound) => sound.key === AUDIO.MUSIC_BOSS,
  )
  if (bossTracks.length === 0) return

  for (const sound of bossTracks) {
    const s = sound as Phaser.Sound.WebAudioSound
    scene.tweens.killTweensOf(s)
    let finished = false
    const finishTrack = () => {
      if (finished) return
      finished = true
      destroyMusicSound(scene, s)
    }

    if (!s.isPlaying) {
      finishTrack()
      continue
    }

    scene.tweens.add({
      targets: s,
      volume: 0,
      duration: bossFadeDuration,
      ease: "Sine.easeIn",
      onComplete: finishTrack,
    })
    scene.time.delayedCall(bossFadeDuration + 100, finishTrack)
  }
}

export function stopBossMusic(scene: Phaser.Scene, duration = FADE_MS): void {
  for (const sound of getMusicSounds(scene)) {
    if (sound.key !== AUDIO.MUSIC_BOSS) continue

    const s = sound as Phaser.Sound.WebAudioSound
    scene.tweens.killTweensOf(s)
    if (!s.isPlaying || duration <= 0) {
      destroyMusicSound(scene, s)
      continue
    }

    let finished = false
    const finishTrack = () => {
      if (finished) return
      finished = true
      destroyMusicSound(scene, s)
    }

    scene.tweens.add({
      targets: s,
      volume: 0,
      duration,
      ease: "Sine.easeIn",
      onComplete: finishTrack,
    })
    scene.time.delayedCall(duration + 100, finishTrack)
  }
}

function ensureMusicTrack(
  scene: Phaser.Scene,
  key: string,
  startMuted = false,
): Phaser.Sound.WebAudioSound | null {
  if (!scene.cache.audio.exists(key)) return null
  cleanupDuplicateMusicTracks(scene)

  const existing = getMusicSounds(scene).find((sound) => sound.key === key) as
    | Phaser.Sound.WebAudioSound
    | undefined
  if (existing) {
    if (!existing.isPlaying) {
      existing.volume = startMuted ? 0 : existing.volume
      existing.play()
    }
    return existing
  }

  const track = scene.sound.add(key, {
    loop: true,
    volume: startMuted ? 0 : effectiveMusic(WAVE_RATIO),
  }) as Phaser.Sound.WebAudioSound
  track.play()
  return track
}

/** Start the music sequence. */
export function startMusicSequence(
  scene: Phaser.Scene,
  isWave = false,
  replaceBossMusic = false,
): void {
  if (!scene.cache.audio.exists(AUDIO.MUSIC_01)) return
  cleanupDuplicateMusicTracks(scene)

  // Check if any music is already playing
  const tracks = getMusicSounds(scene)
  const playingTracks = tracks.filter(
    (s) => (s as Phaser.Sound.WebAudioSound).isPlaying,
  )
  if (playingTracks.length > 0) {
    const isOnlyBossMusic = playingTracks.every(
      (s) => s.key === AUDIO.MUSIC_BOSS,
    )
    if (!replaceBossMusic || !isOnlyBossMusic) return

    fadeMusicOut(scene, FADE_MS, () => startMusicSequence(scene, isWave, false))
    return
  }

  const vol = effectiveMusic(isWave ? WAVE_RATIO : MENU_RATIO)

  const playMainTrack = () => {
    if (scene.sound.getAllPlaying().some((s) => s.key === AUDIO.MUSIC_BOSS)) {
      // Don't interrupt boss music
      return
    }
    const track = ensureMusicTrack(scene, AUDIO.MUSIC_01)
    if (track) track.volume = vol
  }

  const ctx = (scene.sound as unknown as SoundManagerWithContext).context
  if (!ctx || ctx.state === "running") {
    playMainTrack()
  } else {
    let started = false
    const start = () => {
      if (started) return
      started = true
      window.__startMusic = undefined
      playMainTrack()
    }
    window.__startMusic = start
    scene.sound.once("unlocked", start)
  }
}
